import { Text, Container } from '@amelio/ui-library';
import { useEffect, useState } from 'react';

import { CompetencySummary } from './CompetencySummary';
import { EmployeeAvatar } from './EmployeeAvatar';
import { PerformanceCard } from './PerformanceCard';
import { ValuesAlignmentCard } from './ValueAlignmentCard';

import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';
import type { User } from '@/types/directory/User';

import { useUpdatePerformanceScore } from '@/api/cycle-evaluation-services/manager-assessment';
import { useAmelioTranslations } from '@/hooks';
import { getFullName } from '@/utils/user';

interface PostReviewEmployeeInformationProps {
    reviewInstanceId: string;
    cycleInstanceId: string;
    dataTestId?: string;
    employeeName: string;
    employeeUser?: User;
    averageScore: number;
    proficiencyLevels: ProficiencyLevel[];
    employeeAvatarUrl?: string;
    competencyCountByLevel: Record<string, number>;
    includeCompanyValues?: boolean;
    valuesAlignmentPercentage?: number | null;
    isLoading?: boolean;
    overallPerformanceLevelId?: string | null;
    overallPerformanceLevel?:
        | import('@/types/competency/PerformanceScoreLevel').PerformanceScoreLevel
        | null;
    disabled?: boolean;
}

export const PostReviewEmployeeInformation = ({
    reviewInstanceId,
    cycleInstanceId,
    dataTestId = 'post-review-employee-information',
    employeeName,
    employeeUser,
    proficiencyLevels,
    employeeAvatarUrl,
    competencyCountByLevel,
    includeCompanyValues,
    isLoading,
    overallPerformanceLevelId,
    overallPerformanceLevel,
    valuesAlignmentPercentage,
    disabled = false,
}: PostReviewEmployeeInformationProps) => {
    const { t } = useAmelioTranslations();

    const { mutate: updatePerformanceScore } = useUpdatePerformanceScore(
        reviewInstanceId,
        cycleInstanceId,
    );
    const [performanceScore, setPerformanceScore] = useState<string>(
        overallPerformanceLevelId ?? '',
    );

    useEffect(() => {
        if (overallPerformanceLevelId !== undefined) {
            setPerformanceScore(overallPerformanceLevelId || '');
        }
    }, [overallPerformanceLevelId]);

    const employeeFullName = employeeUser ? getFullName(employeeUser) : employeeName;

    const employeeNameForPortrait = employeeName;

    const handlePerformanceScoreChange = (newScore: string) => {
        if (disabled) {
            return;
        }

        setPerformanceScore(newScore);
        if (newScore) {
            updatePerformanceScore({
                reviewInstanceId,
                overallPerformanceLevelId: newScore,
            });
        }
    };

    return (
        <Container
            mb={'md'}
            data-testid={dataTestId}
            className={
                'mt-10 overflow-hidden rounded-[12px] border border-white bg-gradient-to-br from-white/60 to-white/40 p-[24px] shadow-md backdrop-blur-[10px]'
            }
        >
            {/* Title */}
            <div className={'mb-[24px] flex items-center justify-between gap-3'}>
                <Text
                    size={'large'}
                    weight={'bold'}
                    className={'!text-navy text-[18px]'}
                >
                    {t('postReviewResults.portraitOf', {
                        employeeName: employeeNameForPortrait,
                    })}
                </Text>
            </div>

            <div className={'flex flex-col items-start gap-[16px] sm:flex-row sm:gap-[32px]'}>
                <EmployeeAvatar
                    employeeName={employeeFullName}
                    employeeAvatarUrl={employeeAvatarUrl}
                    employeeUser={employeeUser}
                />
                <div
                    className={
                        'grid w-full !grid-cols-1 gap-[12px] sm:!grid-cols-2 sm:gap-[16px] lg:!grid-cols-3'
                    }
                >
                    <PerformanceCard
                        label={t('postReviewResults.performance.label')}
                        value={performanceScore}
                        overallPerformanceLevel={overallPerformanceLevel}
                        onChange={handlePerformanceScoreChange}
                        disabled={disabled}
                    />

                    {includeCompanyValues && valuesAlignmentPercentage !== undefined && (
                        <ValuesAlignmentCard percentage={valuesAlignmentPercentage ?? null} />
                    )}
                </div>
            </div>
            <div className={'my-[24px] h-px bg-white/30'} />

            <CompetencySummary
                proficiencyLevels={proficiencyLevels}
                competencyCountByLevel={competencyCountByLevel}
                isLoading={isLoading}
            />
        </Container>
    );
};

export default PostReviewEmployeeInformation;
